import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, Activity, Flame, ShieldAlert } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => (Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

/** Compact "Perps Market" live-stat strip for the homepage (mirrors Pulse). */
export default function PerpsPulseStrip() {
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [flash, setFlash] = useState({});      // index -> tick, triggers glow
  const prevRef = useRef({});

  useEffect(() => {
    let on = true;
    const load = () => fetch(`${API}/api/pulse`).then((r) => r.json()).then((d) => on && setP(d.perps || d)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => { on = false; clearInterval(t); };
  }, []);

  if (!p) return null;

  const cards = [
    { icon: TrendingUp, label: "Open Positions", value: fmt(p.open_positions), sub: "", to: "/perps" },
    { icon: Activity, label: "24h Volume", value: fmt((p.volume_24h_stxoshi || 0) / 1e6), sub: "STXOSHI", to: "/perps" },
    { icon: Flame, label: "24h Liquidated", value: fmt((p.liquidated_24h_stxoshi || 0) / 1e6), sub: "STXOSHI", to: "/pulse" },
    { icon: ShieldAlert, label: "Closes / Liq 24h", value: fmt(p.closes_liquidations_24h ?? p.closed_24h), sub: "", to: "/pulse" },
  ];

  // Detect which values changed since last poll and pulse-glow those cards.
  const prev = prevRef.current;
  const changed = cards.some((c, i) => prev[i] !== undefined && prev[i] !== c.value);
  if (changed) {
    const next = {};
    cards.forEach((c, i) => { if (prev[i] !== undefined && prev[i] !== c.value) next[i] = Date.now(); });
    if (Object.keys(next).length) setTimeout(() => setFlash((f) => ({ ...f, ...next })), 0);
  }
  cards.forEach((c, i) => { prev[i] = c.value; });

  return (
    <div data-testid="home-perps-strip">
      <style>{`@keyframes perpPulse{0%{box-shadow:0 0 0 0 rgba(249,115,22,.55)}70%{box-shadow:0 0 0 10px rgba(249,115,22,0)}100%{box-shadow:0 0 0 0 rgba(249,115,22,0)}}`}</style>
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3 font-heading flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
        Perps Market · Live
      </p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c, i) => {
          const Icon = c.icon;
          return (
            <Card
              key={i}
              onClick={() => navigate(c.to)}
              style={flash[i] ? { animation: "perpPulse 1s ease-out" } : undefined}
              className="glass-card border-border/60 cursor-pointer transition-all hover:border-orange-500/60 hover:-translate-y-0.5"
              data-testid={`home-perps-card-${i}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-2">
                  <Icon className="w-3.5 h-3.5 text-orange-400" /> {c.label}
                </div>
                <div className={`font-mono text-xl sm:text-2xl font-bold transition-colors duration-500 ${flash[i] ? "text-orange-400" : "text-foreground"}`}>{c.value}</div>
                {c.sub && <div className="text-[10px] text-muted-foreground mt-0.5">{c.sub}</div>}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
